<script setup lang="ts">
import { ProjectAIType, ProjectSettingsField, ProvideElement } from '@/enums.ts'
import { ErrorMessage, Field, type useForm } from 'vee-validate'
import { getProjectAITypes } from '@/config/config.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { computed, inject } from 'vue'
import type { TranslationFunction } from '@/locales/types.ts'
import ChatGptAssistant from '@/components/projects/settings/ai/ChatGptAssistant.vue'
import { ProjectSettingFormKey } from '@/components/projects/settings.types.ts'

const props = defineProps<{
  projectId: number
}>()
const { t } = useTranslations()
const aiTypes = computed(() => getProjectAITypes(t as TranslationFunction))

const { defineField } = inject(ProjectSettingFormKey)
const [ aiType ] = defineField(ProjectSettingsField.AiType)
const [ chatGptInitialTask ]= defineField(ProjectSettingsField.AiChatGptInitialTask)
</script>

<template>
  <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
    <div class="col-span-full">
      <fieldset aria-label="AI settings" class="-space-y-px rounded-md bg-white">
        <label
          v-for="type in aiTypes"
          :key="type.id"
          :value="type.title"
          :aria-label="type.title"
          :aria-description="type.description"
          class="group flex cursor-pointer border border-gray-200 p-4 first:rounded-tl-md first:rounded-tr-md last:rounded-bl-md last:rounded-br-md focus:outline-none has-[:checked]:relative has-[:checked]:border-indigo-200 has-[:checked]:bg-indigo-50"
        >
          <Field
            type="radio"
            name="ai.type"
            :value="type.id"
            v-model="aiType"
            class="relative mt-0.5 size-4 shrink-0 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
          />

          <span class="ml-3 flex flex-col">
            <span
              class="block text-sm font-medium text-gray-900 group-has-[:checked]:text-indigo-900"
            >
              {{ type.title }}
            </span>
            <span class="block text-sm text-gray-500 group-has-[:checked]:text-indigo-700">
              {{ type.description }}
            </span>
          </span>
        </label>
      </fieldset>
    </div>
    <div
      :class="[
        aiType === ProjectAIType.ChatGPTAssistantAPI ? 'block' : 'hidden',
        'col-span-full',
      ]"
    >
      <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 pt-8">
        <div class="col-span-full">
          <ChatGptAssistant :projectId="props.projectId" />
        </div>
      </div>
    </div>
    <div
      :class="[aiType === ProjectAIType.ChatGPTRegular ? 'block' : 'hidden', 'col-span-full']"
    >
      <label for="about" class="block text-sm/6 font-medium text-gray-900">{{ t('settings.ai.initialTask') }}</label>
      <div class="mt-2">
        <Field
          as="textarea"
          id="ai.chatGptInitialTask"
          name="ai.chatGptInitialTask"
          rows="20"
          v-model="chatGptInitialTask"
          class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
        />
      </div>
      <ErrorMessage name="ai.chatGptInitialTask" class="text-sm font-500 text-red-400" />
    </div>
  </div>
</template>
